import { useState } from 'react';
import { Activity, ChevronLeft, ChevronRight, LockKeyhole, Save, ShieldCheck, Sparkles, X, Search } from 'lucide-react';
import { saveEstetikNote } from './data.js';
import { ageFromBirthDate, dateLabel, friendlyError } from './lib/format.js';

// ── ICD-10 codes relevan untuk klinik estetik ──────────────────────────────
const ICD10_ESTETIK = [
  // Acne
  { code: 'L70.0', display: 'Acne vulgaris' },
  { code: 'L70.1', display: 'Acne konglobata' },
  { code: 'L70.3', display: 'Acne tropika' },
  { code: 'L70.8', display: 'Acne lainnya' },
  { code: 'L73.0', display: 'Acne keloid' },
  // Pigmentasi
  { code: 'L81.0', display: 'Hiperpigmentasi pasca inflamasi (PIH)' },
  { code: 'L81.1', display: 'Kloasma (Chloasma)' },
  { code: 'L81.4', display: 'Hiperpigmentasi melanin lainnya (Melasma)' },
  { code: 'L81.5', display: 'Leukoderm, tidak diklasifikasi di tempat lain' },
  { code: 'L81.9', display: 'Gangguan pigmentasi, tidak spesifik' },
  // Fotoaging & Keratosis
  { code: 'L57.0', display: 'Keratosis aktinik' },
  { code: 'L57.3', display: 'Fotoaging (Dermatoheliosis)' },
  { code: 'L57.8', display: 'Perubahan kulit akibat radiasi kronik lainnya' },
  { code: 'L82.1', display: 'Keratosis seboroik' },
  // Parut & Keloid
  { code: 'L90.5', display: 'Kondisi jaringan parut (Scar)' },
  { code: 'L91.0', display: 'Jaringan parut hipertrofik / Keloid' },
  // Dermatitis
  { code: 'L20.9', display: 'Dermatitis atopik, tidak spesifik' },
  { code: 'L30.9', display: 'Dermatitis, tidak spesifik' },
  { code: 'L71.0', display: 'Dermatitis perioral' },
  { code: 'L71.9', display: 'Rosasea, tidak spesifik' },
  // Kista & Tumor Jinak
  { code: 'L72.0', display: 'Kista epidermoid' },
  { code: 'L72.1', display: 'Kista trichilemmal (pilaris)' },
  { code: 'D23.9', display: 'Tumor jinak kulit, tidak spesifik' },
  // Rambut & Kuku
  { code: 'L65.9', display: 'Kerontokan rambut non-sikatrik, tidak spesifik' },
  { code: 'L63.9', display: 'Alopecia areata, tidak spesifik' },
  { code: 'L68.0', display: 'Hirsutisme' },
  { code: 'L60.0', display: 'Kuku tumbuh ke dalam (Onychocryptosis)' },
  { code: 'B35.1', display: 'Onikomikosis (Tinea unguium)' },
  // Lainnya
  { code: 'L29.9', display: 'Pruritus, tidak spesifik' },
  { code: 'L50.9', display: 'Urtikaria, tidak spesifik' },
  { code: 'Q82.5', display: 'Nevus kongenital non-neoplastik' },
  { code: 'L98.9', display: 'Gangguan kulit dan jaringan subkutan, tidak spesifik' },
];

// ── ICD-10 Picker ────────────────────────────────────────────────────────────
function ICD10Picker({ value, onChange, multiple = false, readOnly, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);

  const filtered = ICD10_ESTETIK.filter((item) => {
    const q = query.toLowerCase();
    return !q || item.code.toLowerCase().includes(q) || item.display.toLowerCase().includes(q);
  }).slice(0, 12);

  function select(item) {
    if (multiple) {
      const cur = Array.isArray(value) ? value : [];
      if (!cur.find((v) => v.code === item.code)) onChange([...cur, item]);
    } else {
      onChange(item);
    }
    setQuery(''); setOpen(false);
  }

  function remove(code) {
    if (multiple) onChange((Array.isArray(value) ? value : []).filter((v) => v.code !== code));
    else onChange(null);
  }

  const tags = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  return (
    <div className="icd-picker">
      {tags.length > 0 && (
        <div className="icd-tags">
          {tags.map((v) => (
            <span key={v.code} className="icd-tag">
              <code>{v.code}</code>{v.display}
              {!readOnly && (
                <button type="button" className="icd-tag-remove" onClick={() => remove(v.code)} aria-label="Hapus">×</button>
              )}
            </span>
          ))}
        </div>
      )}
      {!readOnly && (!value || multiple) && (
        <div className="icd-search-wrap">
          <Search size={13} className="icd-search-icon" />
          <input
            className="icd-search-input"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 180)}
            placeholder={placeholder || (multiple ? 'Tambah kode ICD-10…' : 'Cari kode ICD-10…')}
          />
          {open && filtered.length > 0 && (
            <div className="icd-dropdown">
              {filtered.map((item) => (
                <div key={item.code} className="icd-option" onMouseDown={() => select(item)}>
                  <code>{item.code}</code>
                  <span>{item.display}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!readOnly && value && !multiple && (
        <button type="button" className="icd-clear" onClick={() => onChange(null)}>Ganti kode</button>
      )}
    </div>
  );
}

function Field({ label, required = false, wide = false, children, hint }) {
  return (
    <label className={`form-field ${wide ? 'form-field-wide' : ''}`}>
      <span>{label}{required ? ' *' : ''}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function nullable(v) { return v ?? ''; }

const FITZPATRICK = [
  { value: 'I',   label: 'Tipe I',   desc: 'Selalu terbakar · Sangat putih' },
  { value: 'II',  label: 'Tipe II',  desc: 'Mudah terbakar · Putih' },
  { value: 'III', label: 'Tipe III', desc: 'Kadang terbakar · Cokelat muda' },
  { value: 'IV',  label: 'Tipe IV',  desc: 'Jarang terbakar · Cokelat sedang' },
  { value: 'V',   label: 'Tipe V',   desc: 'Sangat jarang terbakar · Cokelat tua' },
  { value: 'VI',  label: 'Tipe VI',  desc: 'Tidak pernah terbakar · Hitam' },
];

const SKIN_AXES = [
  { key: 'texture',      label: 'Tekstur',      opts: [['halus','Halus'],['normal','Normal'],['kasar','Kasar']] },
  { key: 'pores',        label: 'Pori-pori',    opts: [['kecil','Kecil'],['sedang','Sedang'],['besar','Besar']] },
  { key: 'hydration',    label: 'Hidrasi',      opts: [['kering','Kering'],['normal','Normal'],['berminyak','Berminyak'],['kombinasi','Kombinasi']] },
  { key: 'pigmentation', label: 'Pigmentasi',   opts: [['tidak_ada','Tidak ada'],['ringan','Ringan'],['sedang','Sedang'],['berat','Berat']] },
  { key: 'sensitivity',  label: 'Sensitivitas', opts: [['tidak_sensitif','Normal'],['sensitif','Sensitif'],['sangat_sensitif','Sangat sensitif']] },
  { key: 'acne',         label: 'Jerawat',      opts: [['tidak_ada','Tidak ada'],['komedonal','Komedonal'],['inflamasi','Inflamasi'],['berat','Berat']] },
  { key: 'wrinkles',     label: 'Kerutan',      opts: [['tidak_ada','Tidak ada'],['ringan','Ringan'],['sedang','Sedang'],['dalam','Dalam']] },
];

function SkinAxis({ axis, value, onToggle, readOnly }) {
  return (
    <div className="skin-axis">
      <span className="skin-axis-label">{axis.label}</span>
      <div className="skin-axis-options">
        {axis.opts.map(([val, label]) => (
          <button
            key={val} type="button"
            className={`skin-chip ${value === val ? 'skin-chip-active' : ''}`}
            onClick={() => !readOnly && onToggle(axis.key, val)}
            disabled={readOnly}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── SOAP Tab definitions ─────────────────────────────────────────────────────
const SOAP_TABS = [
  { key: 'S', label: 'Subjective',  short: 'S · Keluhan' },
  { key: 'O', label: 'Objective',   short: 'O · Pemeriksaan' },
  { key: 'A', label: 'Assessment',  short: 'A · Asesmen' },
  { key: 'P', label: 'Plan',        short: 'P · Rencana' },
];

// ── Main component ───────────────────────────────────────────────────────────
export function EstetikFormModal({ data, onClose, onSaved, onGoToAction }) {
  const note   = data.estetik_note || {};
  const visit  = data.visit;
  const client = data.client;
  const isExistingFinal = /^(FINAL|AMENDED)$/i.test(note.draft_status || '');

  const matchingStaff = data.staff.find((row) => row.id === note.nakes_id)
    || data.staff.find((row) => row.name === visit.doctor_name);

  const [soapTab, setSoapTab] = useState('S');
  const [form, setForm] = useState({
    nakes_id:          matchingStaff?.id || '',
    // S
    current_complaint: nullable(note.current_complaint),
    treatment_history: nullable(note.treatment_history),
    allergies:         nullable(note.allergies),
    // O
    blood_pressure:    nullable(note.vital_signs?.blood_pressure),
    bpm:               nullable(note.vital_signs?.bpm),
    fitzpatrick_type:  nullable(note.fitzpatrick_type),
    skin_assessment:   note.skin_assessment || {},
    // A
    diagnosis:         nullable(note.diagnosis),
    problem_list:      nullable(note.problem_list),
    icd10_primary:     note.procedure_plan?.icd10_primary   || note.icd10_primary   || null,
    icd10_secondary:   note.procedure_plan?.icd10_secondary || note.icd10_secondary || [],
    // P
    procedures:        nullable(note.procedure_plan?.procedures),
    products:          nullable(note.procedure_plan?.products),
    sessions:          nullable(note.procedure_plan?.sessions),
    follow_up:         nullable(note.procedure_plan?.follow_up),
    action_notes:      nullable(note.action_notes),
  });
  const [correctionMode, setCorrectionMode] = useState(!isExistingFinal);
  const [busy, setBusy]   = useState('');
  const [error, setError] = useState('');
  const readOnly = isExistingFinal && !correctionMode;

  function update(name, value) {
    setForm((cur) => ({ ...cur, [name]: value }));
  }

  function updateSkin(key, value) {
    setForm((cur) => ({
      ...cur,
      skin_assessment: { ...cur.skin_assessment, [key]: value },
    }));
  }

  function validate(finalize) {
    if (finalize && !form.nakes_id) return 'Nakes pemeriksa wajib dipilih sebelum finalisasi.';
    if (finalize && !form.current_complaint.trim()) return 'Keluhan utama wajib diisi sebelum finalisasi.';
    if (finalize && !form.diagnosis.trim()) return 'Diagnosis kulit wajib diisi sebelum finalisasi.';
    if (finalize && !form.icd10_primary?.code) return 'Kode ICD-10 primer wajib dipilih (SATUSEHAT).';
    if (form.blood_pressure.trim() && !/^\d{2,3}\s*\/\s*\d{2,3}$/.test(form.blood_pressure.trim())) {
      return 'Tekanan darah harus format 120/80.';
    }
    if (form.bpm !== '') {
      const bpm = Number(form.bpm);
      if (!Number.isInteger(bpm) || bpm < 20 || bpm > 300) return 'BPM harus angka bulat 20–300.';
    }
    return '';
  }

  async function submit(finalize) {
    const err = validate(finalize);
    setError(err);
    if (err) return;
    if (finalize && !window.confirm(
      isExistingFinal
        ? 'Finalisasi koreksi rekam medis estetik ini? Perubahan akan tercatat dalam audit.'
        : 'Finalisasi pemeriksaan estetik? Pastikan semua data klinis sudah lengkap.'
    )) return;

    setBusy(finalize ? 'final' : 'draft');
    try {
      const payload = {
        nakes_id:          form.nakes_id,
        current_complaint: form.current_complaint,
        treatment_history: form.treatment_history,
        allergies:         form.allergies,
        vital_signs:       { blood_pressure: form.blood_pressure, bpm: form.bpm },
        fitzpatrick_type:  form.fitzpatrick_type,
        skin_assessment:   form.skin_assessment,
        diagnosis:         form.diagnosis,
        problem_list:      form.problem_list,
        procedure_plan: {
          procedures:      form.procedures,
          products:        form.products,
          sessions:        form.sessions ? Number(form.sessions) : null,
          follow_up:       form.follow_up,
          icd10_primary:   form.icd10_primary  || null,
          icd10_secondary: form.icd10_secondary || [],
        },
        action_notes: form.action_notes,
      };
      const result = await saveEstetikNote(visit.id, payload, finalize);
      onSaved(
        result,
        finalize
          ? (isExistingFinal
            ? 'Koreksi pemeriksaan estetik berhasil difinalkan.'
            : 'Pemeriksaan estetik berhasil difinalkan dan siap dilanjutkan ke tindakan.')
          : 'Draft pemeriksaan estetik berhasil disimpan.'
      );
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy('');
    }
  }

  const tabIdx   = SOAP_TABS.findIndex((t) => t.key === soapTab);
  const hasPrev  = tabIdx > 0;
  const hasNext  = tabIdx < SOAP_TABS.length - 1;

  // Tab completion indicators
  const tabDone = {
    S: !!form.current_complaint.trim(),
    O: !!form.fitzpatrick_type,
    A: !!form.diagnosis.trim() && !!form.icd10_primary?.code,
    P: !!form.procedures.trim(),
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="modal-card modal-card-clinical" role="dialog" aria-modal="true" aria-label="Pemeriksaan Estetik">

        {/* ── Header ── */}
        <header className="modal-head">
          <div className="modal-title-icon"><Sparkles size={23} /></div>
          <div className="grow">
            <h2>Pemeriksaan Estetik</h2>
            <p>{visit.visit_number} · {dateLabel(visit.visit_date)} · {note.draft_status || 'BELUM ADA'}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Tutup"><X size={20} /></button>
        </header>

        {/* ── Patient banner ── */}
        <section className="patient-banner">
          <div>
            <span className="eyebrow">Pasien</span>
            <strong>{client.name}</strong>
            <small>{client.client_code} · {client.gender || 'JK belum diisi'} · {ageFromBirthDate(client.birth_date)}</small>
          </div>
          <div>
            <span className="eyebrow">Divisi Estetik</span>
            <strong>{visit.doctor_name || 'Belum ditentukan'}</strong>
            <small>{visit.room_name || 'Ruangan belum diisi'}</small>
          </div>
        </section>

        {/* ── Nakes selector (always visible) ── */}
        <div className="soap-nakes-row">
          <label className="soap-nakes-label" htmlFor="estetik-nakes">Nakes / Terapis *</label>
          <select
            id="estetik-nakes"
            className="soap-nakes-select"
            value={form.nakes_id}
            onChange={(e) => update('nakes_id', e.target.value)}
            disabled={readOnly || !!busy}
          >
            <option value="">— Pilih nakes —</option>
            {data.staff.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </div>

        {/* ── Alerts ── */}
        <div className="modal-alerts">
          {error && <div className="alert alert-danger">{error}</div>}
          {isExistingFinal && !correctionMode && (
            <div className="alert alert-info clinical-lock-notice">
              <LockKeyhole size={18} />
              <span>Rekam medis estetik sudah {note.draft_status}. Data baca-saja.</span>
              {data.can_correct_final && (
                <button className="button button-mini button-quiet" onClick={() => setCorrectionMode(true)}>Mulai koreksi</button>
              )}
            </div>
          )}
          {isExistingFinal && correctionMode && (
            <div className="alert alert-warning clinical-lock-notice">
              <ShieldCheck size={18} />
              <span>Mode koreksi aktif. Versi sebelumnya tetap tersimpan di audit.</span>
            </div>
          )}
        </div>

        {/* ── SOAP Tab bar ── */}
        <nav className="soap-tab-bar">
          {SOAP_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`soap-tab ${soapTab === tab.key ? 'soap-tab-active' : ''} ${tabDone[tab.key] ? 'soap-tab-done' : ''}`}
              onClick={() => setSoapTab(tab.key)}
            >
              <span className="soap-tab-key">{tab.key}</span>
              <span className="soap-tab-label">{tab.label}</span>
              {tabDone[tab.key] && <span className="soap-tab-check">✓</span>}
            </button>
          ))}
        </nav>

        {/* ── Tab content ── */}
        <div className="modal-body soap-tab-body" key={soapTab}>

          {/* S — Subjektif */}
          {soapTab === 'S' && (
            <fieldset className="clinical-fieldset soap-fieldset" disabled={readOnly || !!busy}>
              <legend>S — Subjective · Keluhan Pasien</legend>
              <div className="form-grid">
                <Field label="Keluhan utama" required wide hint="Keluhan estetik, onset, lokasi (wajah/leher/badan), dan gejala penting lain.">
                  <textarea
                    value={form.current_complaint}
                    onChange={(e) => update('current_complaint', e.target.value)}
                    rows="4" maxLength={3000}
                    placeholder="Contoh: Flek hitam di pipi kiri sejak 3 bulan lalu, muncul setelah hamil…"
                  />
                </Field>
                <Field label="Tujuan & ekspektasi perawatan" wide hint="Apa yang diharapkan klien dari kunjungan ini.">
                  <textarea
                    value={form.treatment_history}
                    onChange={(e) => update('treatment_history', e.target.value)}
                    rows="3" maxLength={3000}
                    placeholder="Contoh: Ingin kulit lebih cerah, mengurangi bekas jerawat. Sebelumnya pernah facial laser di klinik lain…"
                  />
                </Field>
                <Field label="Riwayat alergi & sensitivitas" wide hint="Bahan aktif, produk, atau prosedur yang pernah bereaksi.">
                  <textarea
                    value={form.allergies}
                    onChange={(e) => update('allergies', e.target.value)}
                    rows="2" maxLength={2000}
                    placeholder="Contoh: Alergi niacinamide (kemerahan), tidak tahan AHA konsentrasi >10%"
                  />
                </Field>
              </div>
            </fieldset>
          )}

          {/* O — Objektif */}
          {soapTab === 'O' && (
            <fieldset className="clinical-fieldset soap-fieldset" disabled={readOnly || !!busy}>
              <legend>O — Objective · Pemeriksaan Fisik Kulit</legend>
              <div className="form-grid">

                {/* Vital ringkas */}
                <div className="vital-grid">
                  <Field label="Tekanan darah" hint="Format: 120/80">
                    <input
                      value={form.blood_pressure}
                      onChange={(e) => update('blood_pressure', e.target.value)}
                      inputMode="numeric" maxLength={9} placeholder="120/80"
                    />
                  </Field>
                  <Field label="BPM (nadi)" hint="20–300">
                    <input
                      type="number" min="20" max="300" step="1"
                      value={form.bpm}
                      onChange={(e) => update('bpm', e.target.value)}
                      placeholder="72"
                    />
                  </Field>
                </div>

                {/* Fitzpatrick */}
                <Field label="Tipe Fitzpatrick" required wide hint="Tipe kulit berdasarkan respons terhadap paparan sinar matahari — penting untuk menentukan keamanan prosedur.">
                  <div className="fitzpatrick-grid">
                    {FITZPATRICK.map((opt) => (
                      <label key={opt.value} className={`fitzpatrick-card ${form.fitzpatrick_type === opt.value ? 'fitzpatrick-card-active' : ''}`}>
                        <input
                          type="radio" name="fitzpatrick_type" value={opt.value}
                          checked={form.fitzpatrick_type === opt.value}
                          onChange={(e) => update('fitzpatrick_type', e.target.value)}
                          disabled={readOnly}
                        />
                        <strong>{opt.label}</strong>
                        <small>{opt.desc}</small>
                      </label>
                    ))}
                  </div>
                </Field>

                {/* Kondisi kulit */}
                <div className="form-field form-field-wide">
                  <span>Kondisi kulit</span>
                  <div className="skin-assessment-grid">
                    {SKIN_AXES.map((axis) => (
                      <SkinAxis
                        key={axis.key} axis={axis}
                        value={form.skin_assessment[axis.key] || ''}
                        onToggle={updateSkin}
                        readOnly={readOnly || !!busy}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </fieldset>
          )}

          {/* A — Asesmen */}
          {soapTab === 'A' && (
            <fieldset className="clinical-fieldset soap-fieldset" disabled={readOnly || !!busy}>
              <legend>A — Assessment · Diagnosis Kulit</legend>
              <div className="form-grid">
                <Field label="Diagnosis kulit" required wide hint="Contoh: Hiperpigmentasi post-inflamasi, Acne vulgaris grade II, Melasma tipe epidermal">
                  <input
                    value={form.diagnosis}
                    onChange={(e) => update('diagnosis', e.target.value)}
                    maxLength={1000}
                    placeholder="Masukkan diagnosis utama…"
                  />
                </Field>

                <div className="form-field form-field-wide icd-section">
                  <span>
                    Kode ICD-10 Primer
                    <span className="icd-required-badge">Wajib · SATUSEHAT</span>
                  </span>
                  <ICD10Picker
                    value={form.icd10_primary}
                    onChange={(v) => update('icd10_primary', v)}
                    readOnly={readOnly}
                    placeholder="Cari kode ICD-10 diagnosis utama…"
                  />
                  <small>Diagnosis utama yang dilaporkan ke SATUSEHAT via FHIR Condition resource.</small>
                </div>

                <div className="form-field form-field-wide icd-section">
                  <span>
                    Kode ICD-10 Sekunder
                    <span className="icd-optional-badge">Opsional</span>
                  </span>
                  <ICD10Picker
                    value={form.icd10_secondary}
                    onChange={(v) => update('icd10_secondary', v)}
                    multiple readOnly={readOnly}
                    placeholder="Tambah diagnosis sekunder…"
                  />
                  <small>Kondisi penyerta yang relevan dengan kunjungan ini.</small>
                </div>

                <Field label="Daftar masalah prioritas" wide hint="Urutan masalah yang akan ditangani berdasarkan tingkat kepentingan klinis.">
                  <textarea
                    value={form.problem_list}
                    onChange={(e) => update('problem_list', e.target.value)}
                    rows="3" maxLength={3000}
                    placeholder="1. Hiperpigmentasi wajah&#10;2. Bekas jerawat&#10;3. Kulit berminyak"
                  />
                </Field>
              </div>
            </fieldset>
          )}

          {/* P — Plan */}
          {soapTab === 'P' && (
            <fieldset className="clinical-fieldset soap-fieldset" disabled={readOnly || !!busy}>
              <legend>P — Plan · Rencana Perawatan</legend>
              <div className="form-grid">
                <Field label="Prosedur direncanakan" required wide hint="Treatment yang akan dilakukan pada kunjungan ini dan rencana ke depan.">
                  <textarea
                    value={form.procedures}
                    onChange={(e) => update('procedures', e.target.value)}
                    rows="3" maxLength={3000}
                    placeholder="Contoh: Chemical peeling lactic acid 40%, LED therapy 20 mnt, skin brightening mask"
                  />
                </Field>
                <Field label="Produk & bahan aktif" wide hint="Produk utama, bahan aktif, dan konsentrasi yang digunakan.">
                  <textarea
                    value={form.products}
                    onChange={(e) => update('products', e.target.value)}
                    rows="2" maxLength={2000}
                    placeholder="Contoh: Lactic acid 40%, vitamin C serum 20%, SPF 50+ sunscreen"
                  />
                </Field>
                <Field label="Jumlah sesi" hint="Total sesi yang direncanakan untuk mencapai target perawatan.">
                  <input
                    type="number" min="1" max="100" step="1"
                    value={form.sessions}
                    onChange={(e) => update('sessions', e.target.value)}
                    placeholder="Contoh: 6"
                  />
                </Field>
                <Field label="Jadwal follow-up" hint="Kapan klien harus kembali untuk sesi berikutnya.">
                  <input
                    value={form.follow_up}
                    onChange={(e) => update('follow_up', e.target.value)}
                    maxLength={300}
                    placeholder="Contoh: 2 minggu lagi / 14 Agustus 2026"
                  />
                </Field>
                <Field label="Catatan klinis & instruksi home care" wide hint="Instruksi pasca-tindakan, produk yang direkomendasikan untuk di rumah, pantangan.">
                  <textarea
                    value={form.action_notes}
                    onChange={(e) => update('action_notes', e.target.value)}
                    rows="4" maxLength={5000}
                    placeholder="Contoh: Hindari paparan matahari langsung 48 jam, gunakan SPF 50 setiap pagi, jangan dipencet bila ada purging…"
                  />
                </Field>
              </div>
            </fieldset>
          )}
        </div>

        {/* ── Footer: tab navigation + save ── */}
        <footer className="modal-actions soap-footer">
          <div className="soap-nav-group">
            <button
              type="button" className="button button-quiet"
              onClick={() => setSoapTab(SOAP_TABS[tabIdx - 1].key)}
              disabled={!hasPrev}
            >
              <ChevronLeft size={16} /> Sebelumnya
            </button>
            <span className="soap-tab-indicator">
              {tabIdx + 1} / {SOAP_TABS.length} · {SOAP_TABS[tabIdx].label}
            </span>
            <button
              type="button" className="button button-quiet"
              onClick={() => setSoapTab(SOAP_TABS[tabIdx + 1].key)}
              disabled={!hasNext}
            >
              Berikutnya <ChevronRight size={16} />
            </button>
          </div>

          <div className="soap-action-group">
            <button className="button button-quiet" onClick={onClose} disabled={!!busy}>Tutup</button>
            {readOnly ? (
              <button className="button button-primary" onClick={onGoToAction}>
                <Activity size={17} /> Tindakan
              </button>
            ) : (
              <>
                <button className="button button-soft" onClick={() => submit(false)} disabled={!!busy}>
                  <Save size={17} /> {busy === 'draft' ? 'Menyimpan…' : 'Simpan Draft'}
                </button>
                <button className="button button-primary" onClick={() => submit(true)} disabled={!!busy}>
                  <ShieldCheck size={17} /> {busy === 'final' ? 'Memfinalkan…' : (isExistingFinal ? 'Finalisasi Koreksi' : 'Finalisasi Pemeriksaan')}
                </button>
              </>
            )}
          </div>
        </footer>

        {/* ── Styles ── */}
        <style>{`
          /* SOAP Tab bar */
          .soap-tab-bar {
            display: flex;
            border-bottom: 2px solid #e8e2d5;
            background: #faf7f2;
            flex-shrink: 0;
          }
          .soap-tab {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            padding: 10px 8px;
            border: none;
            background: none;
            cursor: pointer;
            border-bottom: 3px solid transparent;
            margin-bottom: -2px;
            transition: background 0.15s;
            position: relative;
          }
          .soap-tab:hover { background: #f0ebe0; }
          .soap-tab-active {
            border-bottom-color: #2f6f4f;
            background: #fff;
          }
          .soap-tab-key {
            font-size: 18px;
            font-weight: 800;
            color: #aaa49a;
            line-height: 1;
          }
          .soap-tab-active .soap-tab-key { color: #2f6f4f; }
          .soap-tab-label {
            font-size: 11px;
            font-weight: 600;
            color: #aaa49a;
            text-transform: uppercase;
            letter-spacing: .04em;
          }
          .soap-tab-active .soap-tab-label { color: #2f6f4f; }
          .soap-tab-done .soap-tab-key { color: #16a34a; }
          .soap-tab-done .soap-tab-label { color: #16a34a; }
          .soap-tab-check {
            position: absolute;
            top: 6px; right: 8px;
            font-size: 11px;
            color: #16a34a;
            font-weight: 700;
          }

          /* Nakes row */
          .soap-nakes-row {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 24px;
            background: #fdf8f0;
            border-bottom: 1px solid #e8e2d5;
            flex-shrink: 0;
          }
          .soap-nakes-label {
            font-size: 12px;
            font-weight: 600;
            color: #6b6555;
            white-space: nowrap;
          }
          .soap-nakes-select {
            flex: 1;
            max-width: 320px;
            padding: 7px 10px;
            border: 1px solid #e8e2d5;
            border-radius: 7px;
            font-size: 14px;
            background: #fff;
          }

          .modal-alerts { padding: 0 24px; }

          /* Tab body */
          .soap-tab-body {
            flex: 1;
            overflow-y: auto;
            padding: 20px 24px;
          }
          .soap-fieldset {
            border: none;
            padding: 0;
            margin: 0;
          }
          .soap-fieldset legend {
            font-size: 13px;
            font-weight: 700;
            color: #2f6f4f;
            letter-spacing: .03em;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 1px solid #e8e2d5;
            width: 100%;
          }

          /* Footer */
          .soap-footer {
            flex-direction: column;
            gap: 10px;
            padding: 12px 20px;
            border-top: 1px solid #e8e2d5;
          }
          .soap-nav-group {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            width: 100%;
          }
          .soap-tab-indicator {
            font-size: 12px;
            color: #888;
            font-weight: 500;
          }
          .soap-action-group {
            display: flex;
            justify-content: flex-end;
            gap: 8px;
            width: 100%;
          }
        `}</style>
      </section>
    </div>
  );
}
